/** The interact half's renderer: a control tree drawn as DOM, and a press turned into a submission.
 *
 * DOM rather than Pixi, and that is the whole point of the split. A control layer needs focus, tab
 * order, an accessible name and a live region for a refusal, and every one of those is free in a
 * native element and expensive in a scene graph. The broadcast canvas needs none of them and must
 * never carry the code that provides them, which is what OVL-007 enforces in CI.
 *
 * There is no component library here, the same as everywhere else in this renderer.
 */

import { useMemo, useRef, useState } from "react";

import type { SceneReport } from "../render/scene/report";
import type { EntityIndex } from "../render/transport/snapshotStore";
import type { SceneDocument } from "./contract";
import { INPUT_KIND } from "./contract";
import type { ControlDocument, ControlNode } from "./controlTree";
import { buildControls, keyClaims } from "./controlTree";
import { keyLabel, useKeyClaims } from "./keys";
import type { FrameStamp, PressState, SubmitPress } from "./press";
import { pressTime, refusalOf } from "./press";
import type { SelectAction, SurfaceMode } from "./select";
import { boundsWithin } from "./select";

export interface ControlSurfaceProps {
  readonly documents: readonly SceneDocument[];
  readonly entities: EntityIndex;
  /** The canvas kinds this connection resolved to. Asked of the welcome, never of a table. */
  readonly canvasKinds: readonly string[];
  readonly submit: SubmitPress;
  /** The last state frame this host took, when it named its own send instant (`RUN-039`). */
  readonly frame?: FrameStamp;
  readonly report?: SceneReport;
  /** `select` reports what was hit and submits nothing. Defaults to `play`. */
  readonly mode?: SurfaceMode;
  /** `controls` draws only what needs DOM, for a host whose render half draws the rest. */
  readonly layer?: "all" | "controls";
  /** A class on the surface root, so a host can place the control layer it is compositing. */
  readonly className?: string;
  readonly onSelect?: (action: SelectAction) => void;
  readonly onHover?: (action: SelectAction) => void;
}

type PressStates = ReadonlyMap<string, PressState>;

function withState(held: PressStates, key: string, state: PressState): PressStates {
  const next = new Map(held);
  next.set(key, state);
  return next;
}

function withoutState(held: PressStates, key: string): PressStates {
  const next = new Map(held);
  next.delete(key);
  return next;
}

/** Every control this person's canvases hold, drawn from the documents and nothing else. */
export function ControlSurface({
  documents,
  entities,
  canvasKinds,
  submit,
  frame,
  report,
  mode = "play",
  layer = "all",
  className,
  onSelect,
  onHover,
}: ControlSurfaceProps) {
  const [pressed, setPressed] = useState<PressStates>(() => new Map<string, PressState>());
  const surface = useRef<HTMLDivElement | null>(null);

  const built = useMemo<readonly ControlDocument[]>(
    () =>
      buildControls(
        documents,
        entities,
        report === undefined ? { canvasKinds } : { canvasKinds, report },
      ),
    [documents, entities, canvasKinds, report],
  );

  const claims = useMemo(
    () => (report === undefined ? keyClaims(built) : keyClaims(built, report)),
    [built, report],
  );

  // The key path has no `disabled` attribute standing in front of it, so it repeats the two checks
  // the button already makes: nothing to submit, and locked.
  function press(node: ControlNode, timeStamp: number | undefined): void {
    if (mode === "select") return;
    const action = node.action;
    if (action === undefined) return;
    if (node.gate?.locked === true) return;

    setPressed((held) => withState(held, node.key, { kind: "sending" }));

    // Handled, never discarded. `then` takes both outcomes right here, so this promise cannot
    // reject unobserved, and `void` marks a promise that has already been answered rather than
    // one that was dropped. A press whose failure went nowhere is the defect this half exists to
    // remove: see Convention_ASubmissionFailureMustReachThePersonWhoPressedIt.
    void submit({ ...action, ...pressTime(frame, timeStamp) }).then(
      () => {
        // Not "it worked": only that there is nothing further to say here. What the press did
        // arrives as state, on the authoritative projection, the same as it does for everyone else.
        setPressed((held) => withoutState(held, node.key));
      },
      (raised: unknown) => {
        setPressed((held) =>
          withState(held, node.key, { kind: "refused", refusal: refusalOf(raised) }),
        );
      },
    );
  }

  useKeyClaims(claims, press);

  const holders = useMemo<ReadonlySet<string>>(
    () => new Set([...claims.values()].map((node) => node.key)),
    [claims],
  );

  /** What the pointer is over, as the host is told it. Never called outside select mode. */
  function hit(event: React.MouseEvent<HTMLElement>, document: ControlDocument): SelectAction {
    const within = event.target instanceof Element ? event.target.closest("[data-node]") : null;
    const box = within ?? event.currentTarget;
    const base = surface.current ?? event.currentTarget;
    return {
      activityId: document.activityId,
      canvasKind: document.canvasKind,
      nodeId: within instanceof HTMLElement ? within.dataset.node : undefined,
      nodeKey: within instanceof HTMLElement ? within.dataset.nodeKey : undefined,
      bounds: boundsWithin(box, base),
    };
  }

  return (
    <div style={surfaceStyle} className={className} ref={surface}>
      {built.map((document) => (
        <section
          key={document.key}
          style={sheet}
          data-activity={document.activityId}
          onClick={
            mode === "select" && onSelect !== undefined
              ? (event) => {
                  onSelect(hit(event, document));
                }
              : undefined
          }
          onMouseMove={
            mode === "select" && onHover !== undefined
              ? (event) => {
                  onHover(hit(event, document));
                }
              : undefined
          }
        >
          {document.nodes.map((node) => (
            <Node
              key={node.key}
              node={node}
              pressed={pressed}
              holders={holders}
              onPress={press}
              layer={layer}
            />
          ))}
        </section>
      ))}
    </div>
  );
}

interface NodeProps {
  node: ControlNode;
  pressed: PressStates;
  /** The keys of the controls that actually hold the key they claim. */
  holders: ReadonlySet<string>;
  onPress: (node: ControlNode, timeStamp: number | undefined) => void;
  /** `controls` draws only what needs DOM, leaving the rest to whatever renders the scene. */
  layer: "all" | "controls";
}

function Node({ node, pressed, holders, onPress, layer }: NodeProps) {
  if (node.kind === INPUT_KIND) {
    return (
      <Control
        node={node}
        state={pressed.get(node.key)}
        shortcut={holders.has(node.key) ? node.keyCode : undefined}
        onPress={onPress}
      />
    );
  }

  if (node.kind === "text") {
    if (layer === "controls") return null;
    return (
      <p style={line} data-node={node.id} data-node-key={node.key}>
        {node.text}
      </p>
    );
  }

  if (node.kind === "bar") {
    return layer === "controls" ? null : <Meter node={node} />;
  }

  return (
    <div style={groupStyle(node)} data-node={node.id} data-node-key={node.key}>
      {node.children.map((child) => (
        <Node
          key={child.key}
          node={child}
          pressed={pressed}
          holders={holders}
          onPress={onPress}
          layer={layer}
        />
      ))}
    </div>
  );
}

/** One pressable control, or one the person cannot use yet, in the host's own words.
 *
 * `shortcut` is the key this control holds, and it never replaces the button beside it.
 */
function Control({
  node,
  state,
  shortcut,
  onPress,
}: {
  node: ControlNode;
  state: PressState | undefined;
  shortcut: string | undefined;
  onPress: (node: ControlNode, timeStamp: number | undefined) => void;
}) {
  const locked = node.gate?.locked === true;
  const label = node.gate?.label ?? "";
  const lockId = `${node.key}/lock`;
  const sending = state?.kind === "sending";
  const refusal = state?.kind === "refused" ? state.refusal : undefined;

  return (
    <div style={control} data-node={node.id} data-node-key={node.key}>
      <button
        type="button"
        aria-label={node.name}
        aria-busy={sending}
        {...(shortcut === undefined ? {} : { "aria-keyshortcuts": keyLabel(shortcut) })}
        {...(locked && label.length > 0 ? { "aria-describedby": lockId } : {})}
        disabled={locked || node.action === undefined}
        onClick={(event) => {
          onPress(node, event.timeStamp);
        }}
        style={locked ? { ...pill, ...lockedPill } : pill}
      >
        {node.name}
      </button>

      {/* aria-keyshortcuts already says this, so it is shown and never announced twice. */}
      {shortcut !== undefined && (
        <span aria-hidden="true" style={note}>
          {keyLabel(shortcut)}
        </span>
      )}

      {locked && label.length > 0 && (
        <span id={lockId} style={note}>
          {label}
        </span>
      )}

      {/* Received here, and that is all it claims. Nothing is said about the outcome, so there is
          nothing to take back when the projection arrives. */}
      {sending && <span style={note}>sent</span>}

      {refusal !== undefined && (
        <p role="alert" style={alert}>
          {refusal.message}
        </p>
      )}
    </div>
  );
}

function Meter({ node }: { node: ControlNode }) {
  const fill = node.fill ?? 0;
  return (
    <div
      role="meter"
      aria-label={node.name}
      aria-valuemin={0}
      aria-valuemax={1}
      aria-valuenow={fill}
      style={meter}
      data-node={node.id}
      data-node-key={node.key}
    >
      <span style={{ ...meterFill, width: `${String(Math.round(fill * 100))}%` }} />
      <span style={meterLabel}>{node.text.length > 0 ? node.text : node.name}</span>
    </div>
  );
}

/** A flowed box. A node the cloud filtered out leaves no hole here, because nothing is placed. */
function groupStyle(node: ControlNode): React.CSSProperties {
  const flow = node.flow;
  if (flow === undefined) return group;
  return {
    ...group,
    display: "flex",
    flexDirection: flow.direction === "row" ? "row" : "column",
    gap: `${String(Math.round(flow.gap * 100))}%`,
  };
}

/** `docs/ui/foundations/type.md`: a moderator panel takes the companion's sizes, 17 px body. */
const surfaceStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 16,
  padding: 16,
  font: "17px ui-monospace, monospace",
  lineHeight: 1.5,
};

const sheet: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

const group: React.CSSProperties = {
  display: "block",
};

const line: React.CSSProperties = {
  margin: 0,
  opacity: 0.8,
};

const control: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

const pill: React.CSSProperties = {
  padding: "12px 16px",
  cursor: "pointer",
  background: "transparent",
  color: "inherit",
  border: "1px solid currentColor",
  borderRadius: 8,
  font: "inherit",
  textAlign: "center",
};

const lockedPill: React.CSSProperties = {
  cursor: "default",
  opacity: 0.45,
};

const note: React.CSSProperties = {
  fontSize: 12,
  opacity: 0.7,
};

const alert: React.CSSProperties = {
  margin: 0,
  fontSize: 13,
  color: "#ff8a80",
};

const meter: React.CSSProperties = {
  position: "relative",
  overflow: "hidden",
  minHeight: 24,
  padding: "4px 8px",
  border: "1px solid currentColor",
  borderRadius: 6,
};

const meterFill: React.CSSProperties = {
  position: "absolute",
  left: 0,
  top: 0,
  bottom: 0,
  background: "currentColor",
  opacity: 0.2,
};

const meterLabel: React.CSSProperties = {
  position: "relative",
  fontSize: 13,
};
